import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiArrowLeft, FiUser, FiAlertCircle, FiPlus, FiX, FiCalendar, FiDollarSign, FiUsers, FiUserPlus, FiTrash2, FiFolder } from 'react-icons/fi';
import toast from 'react-hot-toast';
import ProjectsList from '../Projects/ProjectsList';
import CreateTaskModal from '../MySpace/CreateTaskModal';
import tasksApi from '../../api/tasksApi';
import TaskChecklistIcon from '../shared/TaskChecklistIcon';
import { fmtDate } from '../../utils/istDate';
import { normalizeRole } from '../../finance/auth/permissions';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

export default function EmpProductDetail() {
    const { productId } = useParams();
    const navigate = useNavigate();

    const role = localStorage.getItem('role') || '';
    const normalizedRole = normalizeRole(role);
    const currentUsername = localStorage.getItem('username') || '';
    const isAdminOrTester = normalizedRole === 'ADMIN' || normalizedRole === 'SUPERADMIN' || normalizedRole === 'TESTER';

    // Any user with product access can create a project under this product
    const canCreateProject = true;
    
    const [product, setProduct] = useState(null);
    const [projects, setProjects] = useState([]);
    const [activeTab, setActiveTab] = useState('projects');
    const [productTasks, setProductTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // Product Users Drawer State
    const [showUsersDrawer, setShowUsersDrawer] = useState(false);
    const [productUsers, setProductUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [assignUsername, setAssignUsername] = useState('');
    const [assignLoading, setAssignLoading] = useState(false);

    // Modal state for creating project
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [projectSubmitting, setProjectSubmitting] = useState(false);
    const [projectForm, setProjectForm] = useState({
        projectName: '',
        projectCode: '',
        description: '',
        clientName: '',
        budget: '',
        status: 'PLANNING',
        startDate: '',
        endDate: ''
    });

    // Only Admin/Tester or Product Manager can allot/deassign product team members
    const canManageTeam = isAdminOrTester || (product?.manager_id && (product.manager_id === currentUsername || product.manager_id?.id === currentUsername));

    const fetchProduct = useCallback(async () => {
        const stored = sessionStorage.getItem(`product_${productId}`);
        if (stored) {
            try {
                setProduct(JSON.parse(stored));
                return;
            } catch (e) {
                // parse error, fallback
            }
        }
        try {
            const res = await api.get(`/product/${productId}`);
            const data = res.data?.data || res.data;
            if (data) {
                setProduct(data);
                sessionStorage.setItem(`product_${productId}`, JSON.stringify(data));
            }
        } catch {
            setError('Product details not found. Please return to the product list.');
        }
    }, [productId]);

    const fetchProjects = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await api.get(`/product/${productId}/projects`);
            const rawProj = res.data;
            const projList = Array.isArray(rawProj) ? rawProj
                : Array.isArray(rawProj?.content) ? rawProj.content
                : Array.isArray(rawProj?.data) ? rawProj.data
                : [];
            setProjects(projList);
            if (projList.length === 0) {
                setActiveTab('tasks');
            }
        } catch { 
            setError('Failed to load product projects.'); 
        } finally { 
            setLoading(false); 
        }
    }, [productId]);

    const fetchProductTasks = useCallback(async () => {
        setLoadingTasks(true);
        try {
            const list = await tasksApi.getProductTasks(productId);
            setProductTasks(Array.isArray(list) ? list : []);
        } catch (e) {
            console.error('Failed to load product tasks', e);
            setProductTasks([]);
        } finally {
            setLoadingTasks(false);
        }
    }, [productId]);

    const fetchProductUsers = useCallback(async () => {
        try {
            const res = await api.get(`/product/${productId}/users`);
            const raw = res.data;
            const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.content) ? raw.content : []));
            setProductUsers(list);
        } catch (e) {
            console.error('Failed to load product users', e);
        }
    }, [productId]);

    const fetchAllUsers = useCallback(async () => {
        if (!canManageTeam) return;
        try {
            const res = await api.get('/admin/users');
            const raw = res.data?.data || res.data;
            const list = Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []);
            setAllUsers(list);
        } catch (e) {
            console.error('Failed to load system users', e);
        }
    }, [canManageTeam]);

    useEffect(() => {
        fetchProduct();
        fetchProjects();
        fetchProductTasks();
        fetchProductUsers();
        fetchAllUsers();
    }, [fetchProduct, fetchProjects, fetchProductTasks, fetchProductUsers, fetchAllUsers]);

    const handleSaveTask = async (taskData, files = [], subtasks = []) => {
        try {
            let savedTask;
            if (editingTask) {
                savedTask = await tasksApi.updateTask(editingTask.taskId || editingTask.id, taskData);
                toast.success('Task updated successfully');
            } else {
                savedTask = await tasksApi.createTask(taskData);
                toast.success('Task created successfully');
            }

            const tId = savedTask?.taskId || savedTask?.id;
            if (tId) {
                if (files && files.length > 0) {
                    for (const f of files) {
                        try { await tasksApi.uploadAttachment(tId, f); } catch {}
                    }
                }
                if (subtasks && subtasks.length > 0) {
                    for (const st of subtasks) {
                        if (st.title?.trim()) {
                            try { await tasksApi.createSubtask(tId, { title: st.title.trim() }); } catch {}
                        }
                    }
                }
            }
            setIsTaskModalOpen(false);
            setEditingTask(null);
            fetchProductTasks();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save task');
        }
    };

    const handleAssignUser = async () => {
        if (!assignUsername) return;
        setAssignLoading(true);
        try {
            await api.post('/product/assign', {
                productId: Number(productId),
                username: assignUsername
            });
            toast.success(`User ${assignUsername} allotted to product`);
            setAssignUsername('');
            fetchProductUsers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to assign user to product');
        } finally {
            setAssignLoading(false);
        }
    };

    const handleDeassignUser = async (username) => {
        try {
            await api.post('/product/deassign', {
                productId: Number(productId),
                username: username
            });
            toast.success(`User ${username} removed from product`);
            fetchProductUsers();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to remove user from product');
        }
    };

    const openCreateProjectModal = () => {
        const clientVal = (() => {
            const c = product?.clientName;
            if (!c) return '';
            if (Array.isArray(c)) return typeof c[0] === 'object' ? (c[0].name || '') : c[0];
            return typeof c === 'object' ? (c.name || '') : c;
        })();
        setProjectForm({
            projectName: '',
            projectCode: `PRJ-${Date.now().toString().slice(-4)}`,
            description: '',
            clientName: clientVal || '',
            budget: product?.budget || '',
            status: 'PLANNING',
            startDate: '',
            endDate: ''
        });
        setShowProjectModal(true);
    };

    const handleSaveProject = async (e) => {
        if (e) e.preventDefault();
        if (!projectForm.projectName?.trim()) {
            toast.error('Project name is required.');
            return;
        }
        if (!projectForm.projectCode?.trim()) {
            toast.error('Project code is required.');
            return;
        }
        setProjectSubmitting(true);
        try {
            const body = {
                projectName: projectForm.projectName.trim(),
                projectCode: projectForm.projectCode.trim(),
                description: projectForm.description?.trim() || null,
                clientName: projectForm.clientName?.trim() || null,
                budget: projectForm.budget ? parseFloat(projectForm.budget) : null,
                status: projectForm.status || 'PLANNING',
                startDate: projectForm.startDate || null,
                endDate: projectForm.endDate || null,
                productId: parseInt(productId)
            };
            await api.post('/project/create', body);
            toast.success('Project created successfully!');
            setShowProjectModal(false);
            fetchProjects();
        } catch (err) {
            const msg = err.response?.data;
            if (typeof msg === 'string' && msg.includes('duplicate')) {
                toast.error(`Project code "${projectForm.projectCode}" already exists.`);
            } else {
                toast.error('Failed to create project. Please check required fields.');
            }
        } finally {
            setProjectSubmitting(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-3.5 animate-fade-in pb-12">
            <button 
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors cursor-pointer"
                onClick={() => navigate('/EmpProducts')}
            >
                <FiArrowLeft size={13} /> Back to Products
            </button>

            {error && <div className="p-3 bg-red-50 text-red-600 border border-red-200 rounded-lg text-xs flex items-center gap-2"><FiAlertCircle className="shrink-0" /> {error}</div>}

            {product && (
                <div className="bg-white rounded-xl shadow-2xs border border-slate-200 overflow-hidden">
                    <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                    product.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-slate-100 text-slate-700 border border-slate-200'
                                }`}>
                                    {product.status || 'UNKNOWN'}
                                </span>
                                <h1 className="text-lg sm:text-xl font-bold text-slate-900 tracking-tight truncate">
                                    {product.productname || product.productName || product.name || 'Unnamed Product'}
                                </h1>
                            </div>
                            {product.description && (
                                <p className="text-xs text-slate-500 max-w-2xl leading-relaxed mb-2.5">
                                    {product.description}
                                </p>
                            )}
                            
                            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                                <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/80 text-xs">
                                    <FiUser className="text-slate-400 text-xs" />
                                    <span className="font-medium text-slate-700">Client: {
                                        (() => {
                                            const c = product.clientName;
                                            if (!c) return 'Internal';
                                            if (Array.isArray(c)) return typeof c[0] === 'object' ? (c[0].name || 'Internal') : c[0];
                                            return typeof c === 'object' ? (c.name || 'Internal') : c;
                                        })()
                                    }</span>
                                </div>
                                <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-200/80 text-xs">
                                    <span className="font-bold text-slate-800">${product.budget ? product.budget.toLocaleString() : '0'}</span>
                                    <span className="text-slate-500 font-medium">Budget</span>
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => setActiveTab('projects')}
                                className={`px-3.5 py-2 rounded-xl border text-center min-w-[75px] transition-all cursor-pointer ${
                                    activeTab === 'projects'
                                        ? 'bg-blue-50/80 border-blue-300 text-blue-900 shadow-2xs ring-1 ring-blue-300/40'
                                        : 'bg-slate-50 border-slate-200/80 hover:bg-slate-100'
                                }`}
                            >
                                <div className="text-xl sm:text-2xl font-black text-slate-800 leading-none">{projects.length}</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">Projects</div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('tasks')}
                                className={`px-3.5 py-2 rounded-xl border text-center min-w-[75px] transition-all cursor-pointer ${
                                    activeTab === 'tasks'
                                        ? 'bg-blue-50/80 border-blue-300 text-blue-900 shadow-2xs ring-1 ring-blue-300/40'
                                        : 'bg-slate-50 border-slate-200/80 hover:bg-slate-100'
                                }`}
                            >
                                <div className="text-xl sm:text-2xl font-black text-slate-800 leading-none">{productTasks.length}</div>
                                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mt-0.5">Tasks</div>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Segmented Navigation Bar: Projects vs Product Tasks */}
            <div className="flex items-center justify-between pt-1 pb-0.5 flex-wrap gap-3">
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl border border-slate-200">
                    <button
                        type="button"
                        onClick={() => setActiveTab('projects')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            activeTab === 'projects'
                                ? 'bg-white text-slate-800 shadow-2xs font-bold'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <FiFolder className="text-xs" />
                        <span>Projects</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            activeTab === 'projects' ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-slate-200 text-slate-600'
                        }`}>
                            {projects.length}
                        </span>
                    </button>
                    <button
                        type="button"
                        onClick={() => setActiveTab('tasks')}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                            activeTab === 'tasks'
                                ? 'bg-white text-slate-800 shadow-2xs font-bold'
                                : 'text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        <TaskChecklistIcon className="text-xs" />
                        <span>Tasks</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            activeTab === 'tasks' ? 'bg-blue-50 text-blue-700 font-bold' : 'bg-slate-200 text-slate-600'
                        }`}>
                            {productTasks.length}
                        </span>
                    </button>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowUsersDrawer(true)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-semibold text-xs rounded-lg border border-slate-200 transition-all shadow-2xs cursor-pointer"
                        title="Manage users allotted to this product"
                    >
                        <FiUsers className="text-blue-600 text-xs" />
                        <span>Product Team ({productUsers.length})</span>
                    </button>
                    {activeTab === 'projects' ? (
                        canCreateProject && (
                            <button
                                onClick={openCreateProjectModal}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg transition-all shadow-xs cursor-pointer"
                            >
                                <FiPlus className="text-xs" />
                                <span>New Project</span>
                            </button>
                        )
                    ) : (
                        <button
                            onClick={() => { setEditingTask(null); setIsTaskModalOpen(true); }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg transition-all shadow-xs cursor-pointer"
                        >
                            <FiPlus className="text-xs" />
                            <span>New Task</span>
                        </button>
                    )}
                </div>
            </div>

            {activeTab === 'projects' ? (
                <div className="mt-1">
                    <ProjectsList projects={projects} loading={loading} />
                </div>
            ) : (
                <div className="mt-1 space-y-3">
                    {loadingTasks ? (
                        <div className="py-12 text-center text-slate-500 font-medium w-full">Loading tasks...</div>
                    ) : productTasks.length === 0 ? (
                        <div className="w-full py-16 text-center bg-white rounded-2xl border border-slate-200 border-dashed">
                            <div className="w-14 h-14 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                                <TaskChecklistIcon className="text-2xl" />
                            </div>
                            <h3 className="text-base font-bold text-slate-800 mb-1">No tasks for this product</h3>
                            <p className="text-slate-500 text-xs mb-4 max-w-sm mx-auto">
                                There are no tasks created directly under this product yet.
                            </p>
                            <button
                                onClick={() => { setEditingTask(null); setIsTaskModalOpen(true); }}
                                className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-xl shadow-xs transition-all cursor-pointer"
                            >
                                <FiPlus className="text-xs" />
                                <span>Create Product Task</span>
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Desktop Table View */}
                            <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
                                <table className="w-full text-left border-collapse text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-600 uppercase tracking-wider text-[11px] font-bold">
                                            <th className="py-3 px-4">Task</th>
                                            <th className="py-3 px-4">Project</th>
                                            <th className="py-3 px-4">Assignee</th>
                                            <th className="py-3 px-4">Priority</th>
                                            <th className="py-3 px-4">Status</th>
                                            <th className="py-3 px-4">Due Date</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 font-medium">
                                        {productTasks.map(t => (
                                            <tr 
                                                key={t.taskId || t.id}
                                                onClick={() => navigate(`/my-space/tasks/${t.taskId || t.id}`, { state: { from: `/EmpProducts/${productId}` } })}
                                                className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                            >
                                                <td className="py-3 px-4">
                                                    <div className="font-semibold text-slate-800 group-hover:text-blue-600 transition-colors">
                                                        {t.title}
                                                    </div>
                                                    {t.description && (
                                                        <div className="text-[11px] text-slate-400 line-clamp-1 max-w-md">
                                                            {t.description}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-slate-600">
                                                    {t.projectName || 'Product Task'}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center font-bold text-[9px]">
                                                            {(t.assigneeName || t.username || '?').slice(0, 2).toUpperCase()}
                                                        </div>
                                                        <span className="text-slate-700">{t.assigneeName || t.username || 'Unassigned'}</span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                                        t.priority === 'CRITICAL' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                                        t.priority === 'HIGH' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                        t.priority === 'MEDIUM' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                        'bg-slate-50 text-slate-600 border border-slate-200'
                                                    }`}>
                                                        {t.priority || 'MEDIUM'}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                                                        t.taskStatus === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                        t.taskStatus === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                        t.taskStatus === 'BLOCKED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                                        'bg-slate-100 text-slate-700 border border-slate-200'
                                                    }`}>
                                                        {(t.taskStatus || 'TO_DO').replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="py-3 px-4 text-slate-500 whitespace-nowrap">
                                                    {t.deadLine ? fmtDate(t.deadLine) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile Adaptive Cards View */}
                            <div className="block md:hidden space-y-2.5">
                                {productTasks.map(t => (
                                    <div
                                        key={t.taskId || t.id}
                                        onClick={() => navigate(`/my-space/tasks/${t.taskId || t.id}`, { state: { from: `/EmpProducts/${productId}` } })}
                                        className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-2xs hover:border-blue-300 transition-all cursor-pointer active:scale-[0.99]"
                                    >
                                        <div className="flex items-start justify-between gap-2 mb-1.5">
                                            <h4 className="text-xs font-bold text-slate-800 line-clamp-2">{t.title}</h4>
                                            <span className={`shrink-0 px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                                                t.priority === 'CRITICAL' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                                t.priority === 'HIGH' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
                                                t.priority === 'MEDIUM' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                'bg-slate-50 text-slate-600 border border-slate-200'
                                            }`}>
                                                {t.priority || 'MEDIUM'}
                                            </span>
                                        </div>
                                        {t.description && (
                                            <p className="text-[11px] text-slate-400 line-clamp-1 mb-2.5">{t.description}</p>
                                        )}
                                        <div className="flex items-center justify-between text-[11px] text-slate-500 pt-2 border-t border-slate-100">
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 border border-slate-200 flex items-center justify-center font-bold text-[8px]">
                                                    {(t.assigneeName || t.username || '?').slice(0, 2).toUpperCase()}
                                                </div>
                                                <span className="truncate max-w-[110px]">{t.assigneeName || t.username || 'Unassigned'}</span>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                                                t.taskStatus === 'COMPLETED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
                                                t.taskStatus === 'IN_PROGRESS' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
                                                t.taskStatus === 'BLOCKED' ? 'bg-rose-50 text-rose-700 border border-rose-200' :
                                                'bg-slate-100 text-slate-700 border border-slate-200'
                                            }`}>
                                                {(t.taskStatus || 'TO_DO').replace('_', ' ')}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Right-Side Slide-Over Drawer for Creating New Project (Portaled to document.body) */}
            {showProjectModal && createPortal(
                <div 
                    className="fixed inset-0 bg-slate-900/40 z-[99999] flex justify-end"
                    onClick={() => setShowProjectModal(false)}
                >
                    <div 
                        className="bg-white w-full max-w-lg sm:max-w-xl shadow-2xl flex flex-col h-screen overflow-hidden border-l border-slate-200"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Drawer Header */}
                        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                            <div>
                                <h3 className="text-base sm:text-lg font-bold text-slate-800">Create New Project</h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    Project under <span className="font-semibold text-blue-600">{product?.productname || product?.productName || 'Product'}</span>
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowProjectModal(false)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
                            >
                                <FiX className="text-lg" />
                            </button>
                        </div>

                        {/* Drawer Form Body - Scrollable */}
                        <form onSubmit={handleSaveProject} className="flex-1 flex flex-col overflow-hidden">
                            <div className="p-6 space-y-5 overflow-y-auto flex-1">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Project Name <span className="text-rose-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. SEP Engagement Portal"
                                            value={projectForm.projectName}
                                            onChange={e => setProjectForm({ ...projectForm, projectName: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 shadow-2xs"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Project Code <span className="text-rose-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            required
                                            placeholder="e.g. PRJ-2041"
                                            value={projectForm.projectCode}
                                            onChange={e => setProjectForm({ ...projectForm, projectCode: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 shadow-2xs"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Client Name
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Pfizer, Roche, Internal"
                                            value={projectForm.clientName}
                                            onChange={e => setProjectForm({ ...projectForm, clientName: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Status
                                        </label>
                                        <select
                                            value={projectForm.status}
                                            onChange={e => setProjectForm({ ...projectForm, status: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs cursor-pointer"
                                        >
                                            <option value="PLANNING">Planning</option>
                                            <option value="ACTIVE">Active</option>
                                            <option value="IN_PROGRESS">In Progress</option>
                                            <option value="REVIEW">Review</option>
                                            <option value="COMPLETED">Completed</option>
                                            <option value="ON_HOLD">On Hold</option>
                                            <option value="CANCELLED">Cancelled</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Budget ($)
                                        </label>
                                        <input
                                            type="number"
                                            placeholder="e.g. 50000"
                                            value={projectForm.budget}
                                            onChange={e => setProjectForm({ ...projectForm, budget: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            Start Date
                                        </label>
                                        <input
                                            type="date"
                                            value={projectForm.startDate}
                                            onChange={e => setProjectForm({ ...projectForm, startDate: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs cursor-pointer"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                            End Date
                                        </label>
                                        <input
                                            type="date"
                                            value={projectForm.endDate}
                                            onChange={e => setProjectForm({ ...projectForm, endDate: e.target.value })}
                                            className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs cursor-pointer"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                                        Description
                                    </label>
                                    <textarea
                                        rows={4}
                                        placeholder="Brief outline of the project scope and deliverables..."
                                        value={projectForm.description}
                                        onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                                        className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:border-blue-400 shadow-2xs"
                                    />
                                </div>
                            </div>

                            {/* Drawer Sticky Footer */}
                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-3 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => setShowProjectModal(false)}
                                    className="px-4 py-2 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-200/60 rounded-xl transition-colors cursor-pointer"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={projectSubmitting}
                                    className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs sm:text-sm font-semibold rounded-xl transition-all shadow-xs cursor-pointer flex items-center gap-2"
                                >
                                    {projectSubmitting ? 'Creating...' : 'Create Project'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        
            {/* Product Users Slide-Over Drawer (Portaled to document.body) */}
            {showUsersDrawer && createPortal(
                <div 
                    className="fixed inset-0 bg-slate-900/40 z-[99999] flex justify-end"
                    onClick={() => setShowUsersDrawer(false)}
                >
                    <div 
                        className="bg-white w-full max-w-md shadow-2xl flex flex-col h-screen overflow-hidden border-l border-slate-200"
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Drawer Header */}
                        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50 shrink-0">
                            <div>
                                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                    <FiUsers className="text-blue-600" />
                                    <span>Product Team</span>
                                </h3>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {productUsers.length} member{productUsers.length === 1 ? '' : 's'} allotted to {product?.productname || product?.productName || product?.name || 'this product'}
                                </p>
                            </div>
                            <button 
                                onClick={() => setShowUsersDrawer(false)} 
                                className="text-slate-400 hover:text-slate-600 bg-white shadow-xs border border-slate-200 rounded-full p-1.5 cursor-pointer"
                            >
                                <FiX size={16} />
                            </button>
                        </div>

                        {/* Drawer Body */}
                        <div className="p-6 space-y-5 overflow-y-auto flex-1">
                            {/* Allot New Member Section (If Admin / Superadmin / Tester / Manager) */}
                            {canManageTeam && (
                                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-2.5">
                                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                                        Allot Employee to Product
                                    </label>
                                    <p className="text-[11px] text-slate-500">
                                        Only employees allotted here can be assigned to projects under this product.
                                    </p>
                                    <div className="flex items-center gap-2 pt-1">
                                        <select 
                                            value={assignUsername}
                                            onChange={e => setAssignUsername(e.target.value)}
                                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 shadow-2xs cursor-pointer"
                                        >
                                            <option value="">Select Employee...</option>
                                            {allUsers
                                                .filter(u => !productUsers.some(pu => pu.username === u.username))
                                                .map(u => (
                                                    <option key={u.id || u.username} value={u.username}>
                                                        {u.fullName ? `${u.fullName} (${u.username})` : u.username}
                                                    </option>
                                                ))}
                                        </select>
                                        <button 
                                            onClick={handleAssignUser}
                                            disabled={!assignUsername || assignLoading}
                                            className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-xl transition-colors cursor-pointer flex items-center gap-1.5 text-xs font-semibold shadow-xs shrink-0"
                                        >
                                            <FiUserPlus size={14} />
                                            <span>{assignLoading ? 'Adding...' : 'Allot'}</span>
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Current Member List */}
                            <div className="space-y-2">
                                <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider">
                                    Allotted Product Members ({productUsers.length})
                                </h4>

                                {productUsers.length === 0 ? (
                                    <div className="p-8 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200 text-xs text-slate-500">
                                        No employees currently allotted to this product.
                                    </div>
                                ) : (
                                    <div className="divide-y divide-slate-100 rounded-2xl border border-slate-200 overflow-hidden bg-white">
                                        {productUsers.map((u, i) => (
                                            <div key={i} className="p-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center font-bold text-xs shrink-0 border border-blue-100">
                                                        {u.username ? u.username.substring(0, 2).toUpperCase() : '?'}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-xs font-bold text-slate-800 truncate">
                                                            {u.fullName || u.username}
                                                        </div>
                                                        <div className="text-[11px] text-slate-400 truncate">
                                                            @{u.username} {u.department ? `· ${u.department}` : ''}
                                                        </div>
                                                    </div>
                                                </div>

                                                {canManageTeam && (
                                                    <button 
                                                        onClick={() => handleDeassignUser(u.username)}
                                                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                                                        title="Remove from product"
                                                    >
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Drawer Footer */}
                        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex justify-end shrink-0">
                            <button 
                                onClick={() => setShowUsersDrawer(false)}
                                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Task Creation & Edit Modal */}
            <CreateTaskModal
                isOpen={isTaskModalOpen}
                onClose={() => { setIsTaskModalOpen(false); setEditingTask(null); }}
                onSave={handleSaveTask}
                defaultProductId={Number(productId)}
                initialData={editingTask}
                isEdit={!!editingTask}
            />

        </div>
    );
}
